import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

const LABELS = {
  ar: { horizon: "مستوى الأفق", safe: "المنطقة الآمنة", detect: "اكتشاف الشخصية", head: "مساحة الرأس", look: "مساحة النظر", move: "حرّك يمينًا", key: "الضوء الرئيسي" },
  en: { horizon: "Horizon", safe: "Safe area", detect: "Subject detected", head: "Head room", look: "Look room", move: "Move right", key: "Key light" },
};

// طبقات الواقع المعزز فوق معاينة الكاميرا
export default function AROverlays({ t }) {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.ar;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* شبكة قاعدة الأثلاث + نقاط التقاطع */}
      {t.grid && (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <line x1="33.33%" y1="0" x2="33.33%" y2="100%" stroke="rgba(217,180,106,0.55)" strokeWidth="1" />
          <line x1="66.66%" y1="0" x2="66.66%" y2="100%" stroke="rgba(217,180,106,0.55)" strokeWidth="1" />
          <line x1="0" y1="33.33%" x2="100%" y2="33.33%" stroke="rgba(217,180,106,0.55)" strokeWidth="1" />
          <line x1="0" y1="66.66%" x2="100%" y2="66.66%" stroke="rgba(217,180,106,0.55)" strokeWidth="1" />
          {[["33.33%", "33.33%"], ["66.66%", "33.33%"], ["33.33%", "66.66%"], ["66.66%", "66.66%"]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="4" fill="#4ea8ff" className="focus-pulse" />
          ))}
        </svg>
      )}

      {/* النسبة الذهبية */}
      {t.golden && (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <line x1="38.2%" y1="0" x2="38.2%" y2="100%" stroke="rgba(78,168,255,0.4)" strokeDasharray="6 6" strokeWidth="1" />
          <line x1="61.8%" y1="0" x2="61.8%" y2="100%" stroke="rgba(78,168,255,0.4)" strokeDasharray="6 6" strokeWidth="1" />
          <line x1="0" y1="38.2%" x2="100%" y2="38.2%" stroke="rgba(78,168,255,0.4)" strokeDasharray="6 6" strokeWidth="1" />
        </svg>
      )}

      {/* مستوى الأفق */}
      {t.horizon && (
        <div className="absolute left-4 right-4 top-1/2 flex items-center gap-2">
          <div className="flex-1 h-px bg-ai-blue/70" />
          <span className="text-[10px] text-ai bg-black/40 px-1.5 py-0.5 rounded">{L.horizon}</span>
          <div className="flex-1 h-px bg-ai-blue/70" />
        </div>
      )}

      {/* حدود الكادر + المنطقة الآمنة */}
      {t.frame && (
        <>
          <div className="absolute inset-3 border border-gold/40 rounded-sm" />
          <div className="absolute inset-8 border border-white/20 border-dashed rounded-sm" />
          <span className="absolute top-9 right-9 text-[9px] text-slate-300 bg-black/40 px-1 rounded">{L.safe}</span>
        </>
      )}

      {/* موضع الشخصية + مربع اكتشاف الوجه + مساحة الرأس والنظر */}
      {t.subject && (
        <>
          <motion.div
            className="absolute"
            style={{ left: "22%", top: "30%", width: "26%", height: "44%" }}
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          >
            <div className="w-full h-full border-2 border-ai-blue rounded-lg" style={{ boxShadow: "0 0 14px rgba(78,168,255,0.6)" }} />
            <div className="absolute -top-3 right-0 w-[45%] h-[30%] border border-gold/70 rounded-md" />
            <span className="absolute -bottom-5 right-0 text-[9px] text-ai bg-black/50 px-1 rounded">{L.detect}</span>
          </motion.div>
          <span className="absolute right-[24%] top-[24%] text-[9px] text-gold bg-black/40 px-1 rounded">{L.head}</span>
          <span className="absolute left-[16%] top-[46%] text-[9px] text-gold bg-black/40 px-1 rounded">{L.look}</span>
        </>
      )}

      {/* أسهم حركة الكاميرا */}
      {t.motion && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 text-ai">
          <i className="fa-solid fa-arrow-left-long arrow-slide text-xl" />
          <span className="text-[10px] bg-black/40 px-1.5 py-0.5 rounded">{L.move}</span>
        </div>
      )}

      {/* اتجاه الضوء الرئيسي */}
      {t.light && (
        <div className="absolute top-16 left-6 flex flex-col items-center text-gold">
          <i className="fa-solid fa-sun spin-slow text-2xl" style={{ filter: "drop-shadow(0 0 10px #d9b46a)" }} />
          <i className="fa-solid fa-arrow-down-long text-lg mt-1" />
          <span className="text-[9px] bg-black/40 px-1 rounded mt-1">{L.key}</span>
        </div>
      )}

      {/* خط ارتفاع الكاميرا + خطوط المنظور والعمق */}
      {t.depth && (
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <line x1="10%" y1="100%" x2="45%" y2="42%" stroke="rgba(78,168,255,0.35)" strokeWidth="1" />
          <line x1="90%" y1="100%" x2="55%" y2="42%" stroke="rgba(78,168,255,0.35)" strokeWidth="1" />
          <line x1="0" y1="62%" x2="100%" y2="62%" stroke="rgba(217,180,106,0.4)" strokeDasharray="4 5" strokeWidth="1" />
          <circle cx="50%" cy="42%" r="3" fill="#d9b46a" />
        </svg>
      )}

      {/* مؤشر التركيز */}
      {t.subject && (
        <div className="absolute" style={{ left: "34%", top: "48%" }}>
          <div className="w-10 h-10 border border-gold/80 rounded focus-pulse" />
        </div>
      )}
    </div>
  );
}
